/* The board, a screenful at a time.
 *
 * Paging is done on what the filter left, not on the server: a read brings
 * the whole board back anyway, because the summary rail counts the entire
 * portfolio and the save queue reconciles against every document. Asking
 * the server for a slice would make both of those lie, and buy nothing at
 * this size. So this is a window onto an array that is already in hand.
 *
 * The bar carries its own readout — "13–24 of 37" — because the rail above
 * it counts the whole board and the filter bar counts what the filter left.
 * Neither says which of those you are looking at. */

/* Which numbers to draw. Up to this many pages they all fit, so showing
   them all beats an ellipsis that saves nothing. */
const ALL_UP_TO = 7;

/* The current page, the first, the last, and a neighbour either side; a gap
   stands in for whatever that skips. Returned as numbers and "gap" marks so
   the caller renders buttons and separators without re-deriving which is
   which. */
export function pageList(current, pages) {
  if (pages <= ALL_UP_TO) {
    return Array.from({ length: pages }, (_, i) => i + 1);
  }

  const out = [1];
  const from = Math.max(2, current - 1);
  const to = Math.min(pages - 1, current + 1);

  if (from > 2) out.push("gap-left");
  for (let n = from; n <= to; n++) out.push(n);
  if (to < pages - 1) out.push("gap-right");
  out.push(pages);
  return out;
}

export function BoardPager({ page, pages, from, to, total, onPage }) {
  return (
    <nav className="pager" aria-label="Board pages">
      <span className="pagecount" aria-live="polite">
        {from}–{to} of {total}
      </span>

      <div className="pagenums">
        <button
          className="btn btn-sm"
          type="button"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
        >
          Previous
        </button>

        {pageList(page, pages).map((n) =>
          typeof n === "string" ? (
            <span key={n} className="pagegap" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={n}
              className={"btn btn-sm pagenum" + (n === page ? " on" : "")}
              type="button"
              /* The page you are on is the one control in the bar that does
                 nothing, so it says so rather than being pressable. */
              aria-current={n === page ? "page" : undefined}
              onClick={() => onPage(n)}
            >
              {n}
            </button>
          ),
        )}

        <button
          className="btn btn-sm"
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page >= pages}
        >
          Next
        </button>
      </div>
    </nav>
  );
}
